import { useState, useEffect, useMemo, useRef, useCallback, Fragment } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Plus, Minus, Trash2, Loader2, User, Search, Package, ShoppingCart,
  CreditCard, Percent, DollarSign, FileText, AlertCircle, CheckCircle2,
  Zap, Warehouse, X, Image as ImageIcon, Phone, Mail, AlertTriangle,
  Keyboard, Save, ChevronDown, Barcode, Wallet, Gift, Repeat,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { useUnsavedGuard, guardedNavigate } from '@/lib/navGuard'
import { navegarLista } from '@/lib/keyboard'
import { useFilial } from '@/lib/filial'

interface Cliente {
  id: number
  nome: string
  documento?: string
  email?: string
  telefone?: string
  pessoa_tipo?: string
  ativo: boolean
}

interface Produto {
  id: number
  codigo: string
  ean?: string
  descricao: string
  unidade: string
  preco_venda: number
  preco_custo: number
  estoque_atual: number
  estoque_minimo: number
  tipo: string
  ativo: boolean
  is_industrial?: boolean
  foto_principal_url?: string
}

interface GradeOption {
  id: number
  tipo_id: number
  tipo_codigo: string  // COR | TAMANHO | ...
  tipo_label: string
  codigo: string
  label: string
  hex_cor?: string | null
}

interface UnidadeOption {
  id: number          // id da conversao (null = unidade base)
  unidade: string     // KG, PLACA, BOBINA, ROLO...
  fator: number       // 1 placa = 15 KG → fator=15
  preco_venda: number | null  // preço por unidade alternativa, se cadastrado
  descricao_alt?: string | null
}

interface ItemLinha {
  produto_id: number
  produto_codigo: string
  descricao: string
  unidade_base: string  // unidade padrão do produto (KG, UN, ...)
  preco_base: number    // preço de venda da unidade base
  foto_url?: string
  quantidade: number
  preco_unitario: number
  desconto_unit: number
  estoque_atual: number
  tipo: string  // simples | kit
  is_industrial?: boolean
  // Grades disponíveis pro produto (carregadas ao adicionar). Vazio = produto sem grade.
  grades_disponiveis?: GradeOption[]
  // Selecionadas pelo vendedor (1 por tipo de grade).
  grade_valor_ids?: number[]
  // Unidades alternativas disponíveis (vazio = só unidade base)
  unidades_disponiveis?: UnidadeOption[]
  // null = vende na unidade base; senão = id da ProdutoUnidadeConversao
  conversao_id?: number | null
  // Qualidade exigida pelo cliente (default PRIMEIRA)
  qualidade_exigida?: 'PRIMEIRA' | 'SEGUNDA' | 'TERCEIRA'
  // Saldo agregado por qualidade (preenchido quando modal pré-grava abre)
  saldo_qualidade?: { PRIMEIRA: number; SEGUNDA: number; TERCEIRA: number }
}

interface Deposito {
  id: number
  nome: string
  codigo: string
  tipo: string
  is_default: boolean
  permite_venda: boolean
  ativo: boolean
}

const FORMAS = [
  { id: 'dinheiro', label: 'Dinheiro', icon: DollarSign },
  { id: 'pix', label: 'PIX', icon: Zap },
  { id: 'cartao_credito', label: 'Cartão de Crédito', icon: CreditCard },
  { id: 'cartao_debito', label: 'Cartão de Débito', icon: CreditCard },
  { id: 'boleto', label: 'Boleto', icon: FileText },
  { id: 'prazo', label: 'A prazo', icon: FileText },
  { id: 'cheque', label: 'Cheque', icon: FileText },
  { id: 'bonificacao', label: 'Bonificação', icon: Gift },
  { id: 'troca', label: 'Troca', icon: Repeat },
]

// Hierarquia cumulativa de cobrança: a_prazo>boleto>cheque>avista. O nível do cliente
// é o teto — pode pagar nele e abaixo.
const NIVEL_RANK: Record<string, number> = { avista: 1, cheque: 2, boleto: 3, a_prazo: 4 }
const FORMA_RANK: Record<string, number> = {
  dinheiro: 1, pix: 1, cartao_credito: 1, cartao_debito: 1,
  cheque: 2, boleto: 3, prazo: 4, carteira: 4,
}
const NIVEL_LABEL: Record<string, string> = { avista: 'À vista', cheque: 'Cheque', boleto: 'Boleto', a_prazo: 'A prazo' }
const NIVEL_FORMA_PADRAO: Record<string, string> = { avista: 'dinheiro', cheque: 'cheque', boleto: 'boleto', a_prazo: 'prazo' }

// ── Múltiplos pagamentos (composição PDV-like) ──────────────────────────────
// Linha da composição: cada forma vira título próprio no faturamento.
interface BikeRecebida { marca?: string; modelo?: string; ano?: string; tamanho?: string; numero_serie?: string; cor?: string; observacoes?: string }
interface LinhaPag { forma: string; label: string; valor: number; parcelas: number; primeiro_venc?: string; bike?: BikeRecebida }
const PAG_ICON: Record<string, any> = {
  dinheiro: DollarSign, pix: Zap, cartao_debito: CreditCard, cartao_credito: CreditCard,
  a_vista: Wallet, prazo: FileText, crediario: FileText, boleto: FileText, cheque: FileText,
  recebimento_bike: Repeat,
}
// Cor de cada forma — espelha o FORMA_META do PDV p/ as formas terem identidade
// visual igual à do PDV (botão colorido + texto branco).
const PAG_COLOR: Record<string, string> = {
  dinheiro: 'bg-emerald-600 hover:bg-emerald-700',
  pix: 'bg-teal-600 hover:bg-teal-700',
  cartao_debito: 'bg-blue-600 hover:bg-blue-700',
  cartao_credito: 'bg-violet-600 hover:bg-violet-700',
  a_vista: 'bg-green-700 hover:bg-green-800',
  prazo: 'bg-stone-600 hover:bg-stone-700',
  crediario: 'bg-amber-600 hover:bg-amber-700',
  boleto: 'bg-slate-600 hover:bg-slate-700',
  cheque: 'bg-rose-600 hover:bg-rose-700',
  recebimento_bike: 'bg-fuchsia-600 hover:bg-fuchsia-700',
}
const pagColor = (f: string) => PAG_COLOR[f] || 'bg-ink-600 hover:bg-ink-700'
// Formas exclusivas do caixa imediato (haver do cliente) — não fazem sentido num
// pedido faturado depois. Recebimento de bike (trade-in) É permitido: vira seminova.
const PAG_SO_CAIXA = new Set(['credito_cliente', 'troca'])
const pagIcon = (f: string) => PAG_ICON[f] || CreditCard

export default function NovoPedidoPage() {
  const nav = useNavigate()

  // Cliente
  const [clienteId, setClienteId] = useState<number | ''>('')
  const [clienteSel, setClienteSel] = useState<Cliente | null>(null)  // guarda o selecionado (busca server-side)
  const [buscaCliente, setBuscaCliente] = useState('')
  const [abrirBuscaCliente, setAbrirBuscaCliente] = useState(false)

  // Pagamento
  const [formaPagamento, setFormaPagamento] = useState('dinheiro')
  const [parcelas, setParcelas] = useState(1)
  const [condicaoId, setCondicaoId] = useState<number | ''>('')
  const [pagamentos, setPagamentos] = useState<LinhaPag[]>([])  // composição (múltiplos pagamentos)
  const [vendedorId, setVendedorId] = useState<string>('')  // '' = eu (logado); 'sem' = sem comissão; id = vendedor
  const [buscaVendedor, setBuscaVendedor] = useState('')    // filtro do combobox de vendedor
  const [abrirVendedor, setAbrirVendedor] = useState(false) // dropdown de vendedor aberto
  const [tipoVenda, setTipoVenda] = useState<'fiscal' | 'simples' | 'bonificacao' | 'bonificacao_simples'>('fiscal')  // com nota / sem nota / bonificação
  const [canal, setCanal] = useState<'digitado' | 'celular'>('digitado')  // origem da venda digitada
  const [motivoBonif, setMotivoBonif] = useState('')        // porquê da bonificação (auditoria)
  const [refNumero, setRefNumero] = useState('')            // nº do pedido de venda referente
  const [contaBancariaId, setContaBancariaId] = useState<number | ''>('')      // banco emissor do boleto
  const { data: contasBanco = [] } = useQuery<any[]>({ queryKey: ['contas-banco'], queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data })
  const filialAtualId = useFilial(s => s.filialAtual?.id ?? null)
  const { data: vendedores = [] } = useQuery<any[]>({
    queryKey: ['vendedores-novopedido', filialAtualId],
    queryFn: async () => (await api.get('/cadastros/colaboradores', { params: { tipo: 'vendedor', ativos: true, filial: true } })).data,
  })
  // combobox de vendedor: vazio = mostra todos da filial; digitar filtra por nome/código (rank simples)
  const vendedoresFiltrados = useMemo(() => {
    const q = buscaVendedor.trim().toLowerCase()
    if (!q) return vendedores
    const qn = q.replace(/^0+/, '')
    const rank = (v: any): number => {
      const cod = String(v.codigo || '').toLowerCase()
      const nome = String(v.nome || '').toLowerCase()
      if (cod === q || cod.replace(/^0+/, '') === qn) return 0
      if (cod.startsWith(q) || cod.replace(/^0+/, '').startsWith(qn)) return 1
      if (nome.startsWith(q)) return 2
      if (cod.includes(q) || nome.includes(q)) return 3
      return 99
    }
    return vendedores.map(v => ({ v, r: rank(v) })).filter(x => x.r < 99)
      .sort((a, b) => a.r - b.r || String(a.v.nome).localeCompare(String(b.v.nome))).map(x => x.v)
  }, [vendedores, buscaVendedor])
  // Lista combinada p/ navegação por teclado (mesma ordem do dropdown): as 2 opções
  // fixas + os vendedores filtrados. id '' = eu logado, 'sem' = sem comissão.
  const vendOpcoes = useMemo(() => ([
    { id: '', nome: 'Eu (usuário logado)' },
    { id: 'sem', nome: 'Sem vendedor (sem comissão)' },
    ...vendedoresFiltrados.map((v: any) => ({ id: String(v.id), nome: v.nome })),
  ]), [vendedoresFiltrados])
  const vendedorNome = vendedorId === '' ? 'Eu (usuário logado)'
    : vendedorId === 'sem' ? 'Sem vendedor (sem comissão)'
    : (vendedores.find((v: any) => String(v.id) === vendedorId)?.nome || '')
  const { data: condicoes = [] } = useQuery<any[]>({
    queryKey: ['condicoes-pgto', formaPagamento],
    queryFn: async () => (await api.get('/caixa/condicoes-pagamento', { params: { forma: formaPagamento } })).data,
  })
  // Quando o prazo vem pré-preenchido (cliente), sincroniza nº de parcelas com a condição
  useEffect(() => {
    if (!condicaoId) return
    const c = condicoes.find((x: any) => x.id === condicaoId)
    if (c) setParcelas(Math.max(1, (c.dias || []).length))
  }, [condicoes, condicaoId])
  const [desconto, setDesconto] = useState(0)
  const [frete, setFrete] = useState(0)
  const [observacao, setObservacao] = useState('')

  // Estoque
  const [depositoId, setDepositoId] = useState<number | ''>('')

  // Modal pré-gravação (saldo + qualidade)
  const [modalPreGrava, setModalPreGrava] = useState<{
    aberto: boolean
    carregando: boolean
    forcouProducao: boolean
    motivo: string
    erroSaldo: boolean
  }>({ aberto: false, carregando: false, forcouProducao: false, motivo: '', erroSaldo: false })

  // Itens
  const [itens, setItens] = useState<ItemLinha[]>([])
  // ESC global pergunta antes de sair se há pedido em construção (itens/cliente/observação)
  useUnsavedGuard(itens.length > 0 || !!clienteSel || observacao.trim() !== '')
  const [buscaProd, setBuscaProd] = useState('')
  const [abrirBuscaProd, setAbrirBuscaProd] = useState(false)
  const [destaqueIdx, setDestaqueIdx] = useState(0)   // produto destacado
  const [destaqueCli, setDestaqueCli] = useState(0)   // cliente destacado (teclado)
  const [destaqueVend, setDestaqueVend] = useState(0) // vendedor destacado (teclado)

  const refBuscaCliente = useRef<HTMLInputElement>(null)
  const refBuscaProd = useRef<HTMLInputElement>(null)

  // ============ DADOS ============
  // Busca SERVER-SIDE (são milhares de clientes — não dá pra carregar todos e filtrar no front)
  const { data: clientes = [] } = useQuery<Cliente[]>({
    queryKey: ['clientes-lista', buscaCliente],
    queryFn: async () => (await api.get('/cadastros/pessoas', {
      params: { tipo: 'cliente', q: buscaCliente.trim() || undefined },
    })).data,
  })

  const { data: depositos = [] } = useQuery<Deposito[]>({
    queryKey: ['depositos-select'],
    queryFn: async () => (await api.get('/estoque/depositos')).data,
  })

  const depositosVenda = useMemo(
    () => depositos.filter(d => d.permite_venda && d.ativo),
    [depositos]
  )

  useEffect(() => {
    if (depositosVenda.length > 0 && depositoId === '') {
      const def = depositosVenda.find(d => d.is_default) || depositosVenda[0]
      setDepositoId(def.id)
    }
  }, [depositosVenda, depositoId])

  const { data: produtos = [] } = useQuery<Produto[]>({
    queryKey: ['produtos-busca', buscaProd],
    queryFn: async () => (await api.get('/cadastros/produtos', { params: { q: buscaProd || undefined, finalidade: 'revenda' } })).data,
    enabled: buscaProd.length >= 1,
  })

  // O servidor já filtra por nome/documento (param q) — só limita a exibição.
  const clientesFiltrados = useMemo(() => clientes.slice(0, 40), [clientes])

  // Seleciona cliente (reusado pelo clique e pela navegação por teclado).
  function selecionarCliente(c: any) {
    setClienteId(c.id); setClienteSel(c); setAbrirBuscaCliente(false); setBuscaCliente('')
    // traz o vendedor amarrado ao cliente
    if (c.vendedores_ids?.length) setVendedorId(String(c.vendedores_ids[0]))
    // prefill cobrança (do nível) + prazo (amarrado do cliente)
    const niv = c.nivel_cobranca || 'avista'
    setFormaPagamento(NIVEL_FORMA_PADRAO[niv] || 'dinheiro')
    setCondicaoId(c.condicao_pagamento_id || '')
  }

  // Cliente selecionado: usa o objeto guardado (pode não estar na lista da busca atual).
  const cliente = useMemo(
    () => (clienteSel && clienteSel.id === clienteId ? clienteSel : clientes.find(c => c.id === clienteId)),
    [clienteSel, clientes, clienteId]
  )

  // Crédito (haver) do cliente — gerado por devoluções.
  const { data: creditoCliente } = useQuery<any>({
    queryKey: ['cliente-credito', clienteId],
    queryFn: async () => (await api.get(`/financeiro/creditos/${clienteId}`)).data,
    enabled: !!clienteId,
  })
  const saldoCredito = Number(creditoCliente?.saldo || 0)

  // ============ CALCULOS ============
  const subtotal = useMemo(
    () => itens.reduce((s, i) => s + (i.quantidade * i.preco_unitario - i.desconto_unit), 0),
    [itens]
  )
  const totalSemImposto = subtotal - desconto + frete

  // ICMS-ST do pedido (mesma conta da NF) — total sai COM imposto, igual ao que será salvo/faturado
  const itensST = useMemo(
    () => itens.filter(i => i.produto_id).map(i => ({
      produto_id: i.produto_id, quantidade: i.quantidade,
      total: i.quantidade * i.preco_unitario - i.desconto_unit,
    })),
    [itens]
  )
  const { data: stData } = useQuery<{ valor_st: number }>({
    queryKey: ['pedido-st', clienteId, JSON.stringify(itensST)],
    queryFn: async () => (await api.post('/vendas/calcular-st', { cliente_id: clienteId || null, itens: itensST })).data,
    enabled: itensST.length > 0,
    placeholderData: (p: any) => p,
  })
  const valorST = Number(stData?.valor_st || 0)
  const total = totalSemImposto + valorST

  // Bike recebida na troca: lançada como linha de pagamento (abate o saldo a pagar),
  // mas exibida também como desconto informativo no resumo — igual ao PDV, que
  // mostra "Desconto" (comercial) + "Bicicleta na troca" + "Total de desconto".
  const bikeTotal = useMemo(
    () => pagamentos.filter(p => p.forma === 'recebimento_bike').reduce((s, p) => s + (Number(p.valor) || 0), 0),
    [pagamentos]
  )

  // Alertas: kits e produtos industriais não exigem estoque
  // (industrial é fabricado sob demanda — gera OP proposta ao confirmar)
  // Quando há conversão, qtd está na unidade alternativa — converte pra base.
  function qtdEmBase(i: ItemLinha) {
    if (!i.conversao_id) return i.quantidade
    const conv = i.unidades_disponiveis?.find(u => u.id === i.conversao_id)
    return conv ? i.quantidade * conv.fator : i.quantidade
  }
  const itensEstoqueInsuficiente = itens.filter(
    i => qtdEmBase(i) > i.estoque_atual && i.tipo !== 'kit' && !i.is_industrial
  )
  const itensSobProducao = itens.filter(
    i => qtdEmBase(i) > i.estoque_atual && i.is_industrial
  )
  // Itens com grade pendente (produto tem N tipos de grade, vendedor escolheu < N)
  const itensGradePendente = itens.filter(i => {
    if (!i.grades_disponiveis || i.grades_disponiveis.length === 0) return false
    const tiposDisponiveis = new Set(i.grades_disponiveis.map(g => g.tipo_id))
    const tiposEscolhidos = new Set(
      (i.grade_valor_ids || [])
        .map(vid => i.grades_disponiveis!.find(g => g.id === vid)?.tipo_id)
        .filter((x): x is number => x != null)
    )
    return tiposEscolhidos.size < tiposDisponiveis.size
  })

  const alertas: string[] = []
  if (!clienteId) alertas.push('Selecione o cliente')
  if (itens.length === 0) alertas.push('Adicione ao menos 1 item')
  if (itensEstoqueInsuficiente.length > 0)
    alertas.push(`${itensEstoqueInsuficiente.length} item(ns) com estoque insuficiente`)
  if (itensGradePendente.length > 0)
    alertas.push(`${itensGradePendente.length} item(ns) sem grade selecionada`)
  if (total <= 0) alertas.push('Total deve ser maior que zero')
  // Composição de pagamento: precisa cobrir o total (tolerância de 1 centavo).
  const totalPago = pagamentos.reduce((s, p) => s + (p.valor || 0), 0)
  const saldoPagar = Math.max(0, Math.round((total - totalPago) * 100) / 100)
  const sobraPagar = Math.max(0, Math.round((totalPago - total) * 100) / 100)
  const ehBonificacao = tipoVenda.startsWith('bonificacao')
  if (!ehBonificacao) {
    if (total > 0 && pagamentos.length === 0) alertas.push('Defina o pagamento')
    else if (total > 0 && saldoPagar > 0.01) alertas.push(`Falta pagar ${formatMoney(saldoPagar)}`)
    else if (sobraPagar > 0.01) alertas.push(`Pagamento excede o total em ${formatMoney(sobraPagar)}`)
  }

  const podeEnviar = alertas.length === 0

  // ============ ACOES ============
  async function adicionarProduto(p: Produto) {
    // Carrega grades e unidades de conversão em paralelo
    const [gradesRes, unidadesRes] = await Promise.allSettled([
      api.get(`/cadastros/produtos/${p.id}/grades`),
      api.get(`/cadastros/produtos/${p.id}/unidades-conversao`),
    ])
    const grades: GradeOption[] = gradesRes.status === 'fulfilled'
      ? (gradesRes.value.data || []).map((g: any) => ({
          id: g.id, tipo_id: g.tipo_id, tipo_codigo: g.tipo_codigo,
          tipo_label: g.tipo_label, codigo: g.codigo, label: g.label, hex_cor: g.hex_cor,
        }))
      : []
    const unidades: UnidadeOption[] = unidadesRes.status === 'fulfilled'
      ? (unidadesRes.value.data || [])
          .filter((u: any) => u.ativo !== false)
          .map((u: any) => ({
            id: u.id, unidade: u.unidade, fator: Number(u.fator),
            preco_venda: u.preco_venda != null ? Number(u.preco_venda) : null,
            descricao_alt: u.descricao_alt,
          }))
      : []

    let precoBase = Number(p.preco_venda) || 0
    let descUnit = 0
    // Tabela de preço + campanha de desconto do cliente
    if (clienteId) {
      try {
        const r = await api.get('/comercial/preco-produto', { params: { produto_id: p.id, cliente_id: clienteId } })
        if (r.data && r.data.preco != null) {
          precoBase = Number(r.data.preco)
          if (r.data.origem === 'tabela') toast.success(`Preço da tabela do cliente: ${precoBase.toFixed(2)}`)
        }
      } catch { /* mantém preço padrão */ }
      try {
        const rd = await api.get('/comercial/desconto-produto', { params: { produto_id: p.id, cliente_id: clienteId, quantidade: 1 } })
        const pct = Number(rd.data?.percentual) || 0
        if (pct > 0) {
          descUnit = Number((precoBase * pct / 100).toFixed(2))
          toast.success(`Desconto ${pct}% — ${rd.data.campanha_nome}`)
        }
      } catch { /* sem campanha */ }
    }
    const unidadeBase = (p as any).unidade || 'UN'

    // Sem grade nem conversão alternativa: agrupa qtd na linha existente
    if (grades.length === 0 && unidades.length === 0) {
      const existente = itens.find(i =>
        i.produto_id === p.id &&
        !i.grade_valor_ids?.length &&
        !i.conversao_id
      )
      if (existente) {
        setItens(itens.map(i => i === existente ? { ...i, quantidade: i.quantidade + 1 } : i))
        toast.success(`${p.descricao}: qtd +1`)
        setBuscaProd('')
        setAbrirBuscaProd(false)
        refBuscaProd.current?.focus()
        return
      }
    }

    setItens([...itens, {
      produto_id: p.id,
      produto_codigo: p.codigo,
      descricao: p.descricao,
      unidade_base: unidadeBase,
      preco_base: precoBase,
      foto_url: p.foto_principal_url,
      quantidade: 1,
      preco_unitario: precoBase,
      desconto_unit: descUnit,
      estoque_atual: Number(p.estoque_atual) || 0,
      tipo: p.tipo || 'simples',
      is_industrial: !!p.is_industrial,
      grades_disponiveis: grades,
      grade_valor_ids: [],
      unidades_disponiveis: unidades,
      conversao_id: null,
      qualidade_exigida: 'PRIMEIRA',
    }])
    if (grades.length > 0 || unidades.length > 0) {
      const partes: string[] = []
      if (grades.length > 0) partes.push([...new Set(grades.map(g => g.tipo_label))].join(' + '))
      if (unidades.length > 0) partes.push('unidade')
      toast(`${p.descricao}: escolha ${partes.join(' e ')}`, { icon: '🎨' })
    }
    setBuscaProd('')
    setAbrirBuscaProd(false)
    refBuscaProd.current?.focus()
  }

  function alterarConversao(idx: number, conversao_id: number | null) {
    setItens(itens.map((i, ix) => {
      if (ix !== idx) return i
      if (!conversao_id) {
        // Volta pra unidade base
        return { ...i, conversao_id: null, preco_unitario: i.preco_base }
      }
      const opt = i.unidades_disponiveis?.find(u => u.id === conversao_id)
      if (!opt) return i
      // Preço: se a conversão tem preco_venda, usa; senão calcula proporcional
      const novoPreco = opt.preco_venda != null
        ? opt.preco_venda
        : i.preco_base * opt.fator
      return { ...i, conversao_id, preco_unitario: novoPreco }
    }))
  }

  function alterarQualidadeExigida(idx: number, q: 'PRIMEIRA' | 'SEGUNDA' | 'TERCEIRA') {
    setItens(itens.map((i, ix) => ix === idx ? { ...i, qualidade_exigida: q } : i))
  }

  // Abre o modal pré-gravação carregando saldo de cada item industrial
  async function iniciarSalvar() {
    // Atalho: se NÃO há item industrial, modal não traz nada útil — pula direto
    const temIndustrial = itens.some(i => i.is_industrial)
    if (!temIndustrial) {
      criar.mutate()
      return
    }

    setModalPreGrava(s => ({ ...s, aberto: true, carregando: true, forcouProducao: false, motivo: '', erroSaldo: false }))
    let houveErro = false
    const novos = await Promise.all(itens.map(async (i) => {
      if (!i.is_industrial) return i
      try {
        const r = await api.get(`/vendas/saldo-disponivel/${i.produto_id}`)
        return { ...i, saldo_qualidade: r.data.saldo_por_qualidade }
      } catch (e: any) {
        houveErro = true
        toast.error(`Falha ao consultar saldo de ${i.descricao}: ${e?.message || 'erro de rede'}`)
        return { ...i, saldo_qualidade: undefined }
      }
    }))
    setItens(novos)
    setModalPreGrava(s => ({ ...s, carregando: false, erroSaldo: houveErro }))
  }

  function alterarGrade(idx: number, tipo_id: number, valor_id: number | null) {
    setItens(itens.map((i, ix) => {
      if (ix !== idx) return i
      const outras = (i.grade_valor_ids || []).filter(gvid => {
        const opt = i.grades_disponiveis?.find(g => g.id === gvid)
        return opt && opt.tipo_id !== tipo_id
      })
      const novas = valor_id ? [...outras, valor_id] : outras
      return { ...i, grade_valor_ids: novas }
    }))
  }

  function removerItem(idx: number) {
    setItens(itens.filter((_, i) => i !== idx))
  }

  function alterarQuantidade(idx: number, qtd: number) {
    if (qtd < 0.001) return
    setItens(itens.map((i, ix) => ix === idx ? { ...i, quantidade: qtd } : i))
  }

  function alterarPreco(idx: number, preco: number) {
    setItens(itens.map((i, ix) => ix === idx ? { ...i, preco_unitario: preco } : i))
  }

  function alterarDescontoUnit(idx: number, desc: number) {
    setItens(itens.map((i, ix) => ix === idx ? { ...i, desconto_unit: desc } : i))
  }

  const criar = useMutation({
    mutationFn: async () => {
      const obsFinal = modalPreGrava.forcouProducao && modalPreGrava.motivo.trim()
        ? `${observacao}\n\n[FORÇOU PRODUÇÃO COM ESTOQUE] ${modalPreGrava.motivo.trim()}`.trim()
        : observacao
      const payload = {
        cliente_id: clienteId,
        deposito_id: depositoId,
        tipo_venda: tipoVenda,
        canal_origem: canal,
        motivo_bonificacao: tipoVenda.startsWith('bonificacao') ? (motivoBonif || null) : undefined,
        pedido_referente_numero: tipoVenda.startsWith('bonificacao') && refNumero.trim() ? Number(refNumero.trim()) : undefined,
        forma_pagamento: (pagamentos[0]?.forma) || formaPagamento,
        conta_bancaria_id: (pagamentos.some(p => p.forma === 'boleto') && contaBancariaId) ? Number(contaBancariaId) : undefined,
        parcelas,
        condicao_pagamento_id: condicaoId || undefined,
        // Composição de múltiplos pagamentos (omitida em bonificação — é "dado").
        pagamentos: (!tipoVenda.startsWith('bonificacao') && pagamentos.length > 0)
          ? pagamentos.map(p => ({ forma: p.forma, valor: p.valor, parcelas: p.parcelas, primeiro_venc: p.primeiro_venc || null, bike: p.bike || null }))
          : undefined,
        vendedor_cad_id: (vendedorId && vendedorId !== 'sem') ? Number(vendedorId) : undefined,
        desconto,
        frete,
        observacao: obsFinal,
        itens: itens.map(i => ({
          produto_id: i.produto_id,
          quantidade: i.quantidade,
          preco_unitario: i.preco_unitario,
          desconto: i.desconto_unit,
          grade_valor_ids: (i.grade_valor_ids && i.grade_valor_ids.length) ? i.grade_valor_ids : undefined,
          conversao_id: i.conversao_id || undefined,
          qualidade_exigida: i.qualidade_exigida || 'PRIMEIRA',
        })),
      }
      return (await api.post('/vendas', payload)).data
    },
    onSuccess: (data: any) => {
      toast.success(`Pedido #${data.numero} criado!`)
      nav('/vendas')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao criar pedido'),
  })

  // ============ ATALHOS DE TECLADO ============
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      // F2 = focar cliente
      if (e.key === 'F2') { e.preventDefault(); setAbrirBuscaCliente(true); setTimeout(() => refBuscaCliente.current?.focus(), 50) }
      // F3 = focar produto
      if (e.key === 'F3') { e.preventDefault(); refBuscaProd.current?.focus() }
      // F12 = criar pedido
      if (e.key === 'F12') { e.preventDefault(); if (podeEnviar) iniciarSalvar() }
      // Esc = fecha buscas + modal pré-gravação
      if (e.key === 'Escape') {
        setAbrirBuscaCliente(false)
        setAbrirBuscaProd(false)
        setModalPreGrava(s => s.aberto ? { ...s, aberto: false } : s)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [podeEnviar])

  // ============ RENDER ============
  return (
    <div className="p-4 lg:p-6 max-w-[1400px] mx-auto">
      {/* Cabeçalho */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => guardedNavigate(() => nav(-1))}
          className="p-2 rounded-lg hover:bg-ink-100">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-ink-900 flex items-center gap-2">
            <ShoppingCart size={22} /> Novo Pedido
          </h1>
          <p className="text-xs text-ink-500 flex items-center gap-3">
            <span className="flex items-center gap-1"><Keyboard size={11} /> atalhos: F2 cliente · F3 produto · F12 criar</span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* ============ COLUNA ESQUERDA (dados + itens) ============ */}
        <div className="lg:col-span-2 space-y-4">

          {/* --- CLIENTE --- */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <User size={14} /> Cliente
                <span className="text-[10px] bg-ink-100 text-ink-500 px-1 rounded font-mono">F2</span>
              </h3>
              {cliente && (
                <button onClick={() => { setClienteId(''); setClienteSel(null); setBuscaCliente('') }}
                  className="text-xs text-ink-500 hover:text-red-600 flex items-center gap-1">
                  <X size={12} /> Remover
                </button>
              )}
            </div>

            {!cliente ? (
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                <input
                  ref={refBuscaCliente}
                  value={buscaCliente}
                  onChange={e => { setBuscaCliente(e.target.value); setAbrirBuscaCliente(true); setDestaqueCli(0) }}
                  onFocus={() => setAbrirBuscaCliente(true)}
                  // ↓↑ navega, Enter seleciona o destacado, Esc fecha
                  onKeyDown={navegarLista(clientesFiltrados, destaqueCli, setDestaqueCli,
                    selecionarCliente, () => setAbrirBuscaCliente(false))}
                  placeholder="Buscar por nome, CPF/CNPJ ou telefone..."
                  className="input pl-9"
                  autoComplete="off"
                />
                {abrirBuscaCliente && clientesFiltrados.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-10 max-h-80 overflow-y-auto">
                    {clientesFiltrados.map((c, idx) => (
                      <button key={c.id}
                        onClick={() => selecionarCliente(c)}
                        onMouseEnter={() => setDestaqueCli(idx)}
                        ref={el => { if (idx === destaqueCli) el?.scrollIntoView({ block: 'nearest' }) }}
                        className={`w-full px-3 py-2 text-left border-b last:border-0 flex items-center gap-3 ${idx === destaqueCli ? 'bg-brand-50' : 'hover:bg-ink-50'}`}>
                        <div className="w-8 h-8 rounded-full bg-ink-200 text-ink-700 flex items-center justify-center text-xs font-bold">
                          {c.nome.slice(0, 2).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{c.nome}</p>
                          <p className="text-xs text-ink-500 flex items-center gap-2">
                            <span className="text-[10px] bg-ink-100 px-1 rounded uppercase">{c.pessoa_tipo}</span>
                            {c.documento && <span className="font-mono">{c.documento}</span>}
                            {c.telefone && <span>{c.telefone}</span>}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
              <div className="flex items-center gap-3 p-2 bg-brand-50 rounded-lg border border-brand-200">
                <div className="w-10 h-10 rounded-full bg-brand-200 text-brand-800 flex items-center justify-center text-sm font-bold">
                  {cliente.nome.slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-ink-900">{cliente.nome}</p>
                  <div className="flex items-center gap-3 text-xs text-ink-600">
                    {cliente.documento && <span className="font-mono">{cliente.documento}</span>}
                    {cliente.telefone && <span className="flex items-center gap-1"><Phone size={10} />{cliente.telefone}</span>}
                    {cliente.email && <span className="flex items-center gap-1"><Mail size={10} />{cliente.email}</span>}
                  </div>
                </div>
                <CheckCircle2 size={18} className="text-emerald-600" />
              </div>
              {saldoCredito > 0 && (
                <div className="mt-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium">
                  <Wallet size={13} />
                  Crédito disponível: {formatMoney(saldoCredito)}
                  <span className="text-emerald-600 font-normal">— abata o título no Financeiro</span>
                </div>
              )}
              </>
            )}
          </div>

          {/* --- BUSCA PRODUTO --- */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Package size={14} /> Adicionar produto
                <span className="text-[10px] bg-ink-100 text-ink-500 px-1 rounded font-mono">F3</span>
              </h3>
              <div className="flex items-center gap-1 text-xs">
                <Warehouse size={12} className="text-ink-400" />
                <select
                  value={depositoId}
                  onChange={e => setDepositoId(Number(e.target.value))}
                  className="text-xs bg-transparent border border-ink-200 rounded px-2 py-0.5">
                  {depositosVenda.map(d => (
                    <option key={d.id} value={d.id}>{d.nome}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="relative">
              <Barcode size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
              <input
                ref={refBuscaProd}
                value={buscaProd}
                onChange={e => { setBuscaProd(e.target.value); setAbrirBuscaProd(true); setDestaqueIdx(0) }}
                onFocus={() => setAbrirBuscaProd(buscaProd.length > 0)}
                // ↓↑ navega, Enter adiciona o destacado, Esc fecha. Usa a MESMA lista
                // exibida (15 visíveis) p/ o índice não apontar p/ item invisível.
                onKeyDown={navegarLista(produtos.slice(0, 15), destaqueIdx, setDestaqueIdx,
                  adicionarProduto, () => setAbrirBuscaProd(false))}
                placeholder="Digite código, EAN ou descrição..."
                className="input pl-9"
                autoComplete="off"
              />
              {abrirBuscaProd && buscaProd.length >= 1 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-20 max-h-96 overflow-y-auto">
                  {produtos.length === 0 ? (
                    <div className="p-4 text-center text-sm text-ink-500">Nenhum produto encontrado</div>
                  ) : (
                    produtos.slice(0, 15).map((p, idx) => (
                      <button
                        key={p.id}
                        onClick={() => adicionarProduto(p)}
                        onMouseEnter={() => setDestaqueIdx(idx)}
                        ref={el => { if (idx === destaqueIdx) el?.scrollIntoView({ block: 'nearest' }) }}
                        className={`w-full px-3 py-2 text-left border-b last:border-0 flex items-center gap-3 ${
                          idx === destaqueIdx ? 'bg-brand-50' : 'hover:bg-ink-50'
                        }`}>
                        <div className="w-10 h-10 rounded bg-ink-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {p.foto_principal_url ? (
                            <img src={p.foto_principal_url} className="w-full h-full object-cover" />
                          ) : (
                            <ImageIcon size={18} className="text-ink-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{p.descricao}</p>
                          <div className="flex items-center gap-2 text-xs text-ink-500">
                            <span className="font-mono">{p.codigo}</span>
                            {p.tipo === 'kit' && <span className="bg-amber-100 text-amber-800 text-[9px] px-1 rounded font-bold">KIT</span>}
                            <span>{p.unidade}</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-bold text-emerald-700">{formatMoney(p.preco_venda)}</p>
                          <p className={`text-[10px] ${
                            Number(p.estoque_atual) <= 0 ? 'text-red-600 font-bold'
                            : Number(p.estoque_atual) < Number(p.estoque_minimo) ? 'text-amber-600'
                            : 'text-ink-500'
                          }`}>
                            estq: {p.estoque_atual}
                          </p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* --- TABELA DE ITENS --- */}
          <div className="card overflow-hidden">
            <div className="px-4 py-2 border-b bg-ink-50 flex items-center justify-between">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <ShoppingCart size={14} />
                Itens do pedido
                <span className="text-xs font-normal text-ink-500">({itens.length})</span>
              </h3>
              {itens.length > 0 && (
                <button onClick={() => { if (confirm('Limpar todos os itens?')) setItens([]) }}
                  className="text-xs text-red-600 hover:underline flex items-center gap-1">
                  <Trash2 size={12} /> Limpar
                </button>
              )}
            </div>
            {itens.length === 0 ? (
              <div className="p-10 text-center text-ink-400">
                <Package size={36} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nenhum item adicionado.</p>
                <p className="text-xs mt-1">Use a busca acima ou pressione <kbd className="bg-ink-100 px-1 rounded">F3</kbd></p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-ink-50 text-xs text-ink-600">
                  <tr>
                    <th className="text-left p-2 w-12"></th>
                    <th className="text-left p-2">Produto</th>
                    <th className="text-right p-2 w-24">Qtd</th>
                    <th className="text-right p-2 w-32">Preço</th>
                    <th className="text-right p-2 w-28">Desc.</th>
                    <th className="text-right p-2 w-28">Total</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {itens.map((item, idx) => {
                    const tot = item.quantidade * item.preco_unitario - item.desconto_unit
                    const faltaEstoque = item.quantidade > item.estoque_atual && item.tipo !== 'kit'
                    const semEstoque = faltaEstoque && !item.is_industrial
                    const sobProducao = faltaEstoque && !!item.is_industrial
                    const temGrade = (item.grades_disponiveis?.length || 0) > 0
                    const temUnidades = (item.unidades_disponiveis?.length || 0) > 0
                    const temExtras = temGrade || temUnidades
                    // Agrupa grades por tipo
                    const tipos = temGrade
                      ? Array.from(new Map(
                          item.grades_disponiveis!.map(g => [g.tipo_id, { id: g.tipo_id, label: g.tipo_label, codigo: g.tipo_codigo }])
                        ).values())
                      : []
                    const gradePendente = temGrade && tipos.some(t => {
                      const escolhida = (item.grade_valor_ids || []).some(vid =>
                        item.grades_disponiveis!.find(g => g.id === vid)?.tipo_id === t.id
                      )
                      return !escolhida
                    })
                    const convAtual = item.conversao_id
                      ? item.unidades_disponiveis?.find(u => u.id === item.conversao_id)
                      : null
                    const unidadeRotulo = convAtual ? convAtual.unidade : item.unidade_base
                    return (
                      <Fragment key={idx}>
                      <tr className={semEstoque ? 'bg-red-50/40' : sobProducao ? 'bg-sky-50/40' : ''}>
                        <td className="p-2">
                          <div className="w-10 h-10 rounded bg-ink-100 flex items-center justify-center overflow-hidden">
                            {item.foto_url ? (
                              <img src={item.foto_url} className="w-full h-full object-cover" />
                            ) : (
                              <ImageIcon size={16} className="text-ink-400" />
                            )}
                          </div>
                        </td>
                        <td className="p-2">
                          <p className="font-medium text-ink-900 truncate max-w-xs">{item.descricao}</p>
                          <div className="flex items-center gap-2 text-xs text-ink-500">
                            <span className="font-mono">{item.produto_codigo}</span>
                            {item.tipo === 'kit' && <span className="bg-amber-100 text-amber-800 text-[9px] px-1 rounded font-bold">KIT</span>}
                            {item.is_industrial && <span className="bg-sky-100 text-sky-800 text-[9px] px-1 rounded font-bold">INDUSTRIAL</span>}
                            {sobProducao && (
                              <span className="text-sky-700 flex items-center gap-0.5 font-semibold">
                                ⚙ faltam {(item.quantidade - item.estoque_atual).toFixed(3)} → será produzido
                              </span>
                            )}
                            {semEstoque && (
                              <span className="text-red-600 flex items-center gap-0.5 font-semibold">
                                <AlertTriangle size={10} /> estoque: {item.estoque_atual}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-2">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => alterarQuantidade(idx, item.quantidade - 1)}
                              className="w-6 h-6 rounded bg-ink-100 hover:bg-ink-200 flex items-center justify-center">
                              <Minus size={12} />
                            </button>
                            <input
                              type="number"
                              value={item.quantidade}
                              step="0.001"
                              min="0.001"
                              onChange={e => alterarQuantidade(idx, Number(e.target.value))}
                              className="w-14 text-center bg-transparent border-b border-ink-200 focus:border-brand-500 outline-none text-sm"
                            />
                            <button onClick={() => alterarQuantidade(idx, item.quantidade + 1)}
                              className="w-6 h-6 rounded bg-ink-100 hover:bg-ink-200 flex items-center justify-center">
                              <Plus size={12} />
                            </button>
                            <span className="text-[10px] text-ink-500 ml-0.5 min-w-[28px]">{unidadeRotulo}</span>
                          </div>
                        </td>
                        <td className="p-2 text-right">
                          <input
                            type="number"
                            value={item.preco_unitario}
                            step="0.01"
                            onChange={e => alterarPreco(idx, Number(e.target.value))}
                            className="w-24 text-right bg-transparent border-b border-ink-200 focus:border-brand-500 outline-none text-sm"
                          />
                        </td>
                        <td className="p-2 text-right">
                          <input
                            type="number"
                            value={item.desconto_unit}
                            step="0.01"
                            min="0"
                            onChange={e => alterarDescontoUnit(idx, Number(e.target.value))}
                            className="w-20 text-right bg-transparent border-b border-ink-200 focus:border-brand-500 outline-none text-sm text-red-600"
                          />
                        </td>
                        <td className="p-2 text-right font-bold text-ink-900">{formatMoney(tot)}</td>
                        <td className="p-2">
                          <button onClick={() => removerItem(idx)}
                            className="p-1 text-ink-400 hover:text-red-600 rounded hover:bg-red-50">
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                      {temExtras && (
                        <tr className={(semEstoque ? 'bg-red-50/40 ' : sobProducao ? 'bg-sky-50/40 ' : '') + 'border-t-0'}>
                          <td colSpan={7} className="px-2 pb-2">
                            <div className={'rounded-lg border p-2 ' + (gradePendente ? 'border-amber-300 bg-amber-50/60' : 'border-ink-200 bg-white')}>
                              <div className="flex flex-wrap gap-3">
                                {temUnidades && (
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[11px] font-semibold text-ink-600 uppercase tracking-wide">Unidade:</span>
                                    <div className="flex gap-1 flex-wrap">
                                      <button
                                        type="button"
                                        onClick={() => alterarConversao(idx, null)}
                                        className={
                                          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border transition ' +
                                          (!item.conversao_id
                                            ? 'bg-brand-600 text-white border-brand-600 shadow-sm'
                                            : 'bg-white text-ink-700 border-ink-300 hover:border-brand-400')
                                        }
                                      >
                                        {item.unidade_base}
                                      </button>
                                      {item.unidades_disponiveis!.map(u => {
                                        const ativo = item.conversao_id === u.id
                                        return (
                                          <button
                                            key={u.id}
                                            type="button"
                                            onClick={() => alterarConversao(idx, u.id)}
                                            className={
                                              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border transition ' +
                                              (ativo
                                                ? 'bg-brand-600 text-white border-brand-600 shadow-sm'
                                                : 'bg-white text-ink-700 border-ink-300 hover:border-brand-400')
                                            }
                                            title={`1 ${u.unidade} = ${u.fator} ${item.unidade_base}`}
                                          >
                                            {u.unidade}
                                            <span className="opacity-60 text-[10px]">×{u.fator}</span>
                                          </button>
                                        )
                                      })}
                                    </div>
                                  </div>
                                )}
                                {tipos.map(tipo => {
                                  const opcoes = item.grades_disponiveis!.filter(g => g.tipo_id === tipo.id)
                                  const selecionada = (item.grade_valor_ids || []).find(vid =>
                                    opcoes.some(o => o.id === vid)
                                  )
                                  return (
                                    <div key={tipo.id} className="flex items-center gap-1.5">
                                      <span className="text-[11px] font-semibold text-ink-600 uppercase tracking-wide">{tipo.label}:</span>
                                      <div className="flex gap-1 flex-wrap">
                                        {opcoes.map(opt => {
                                          const ativo = selecionada === opt.id
                                          return (
                                            <button
                                              key={opt.id}
                                              type="button"
                                              onClick={() => alterarGrade(idx, tipo.id, ativo ? null : opt.id)}
                                              className={
                                                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border transition ' +
                                                (ativo
                                                  ? 'bg-brand-600 text-white border-brand-600 shadow-sm'
                                                  : 'bg-white text-ink-700 border-ink-300 hover:border-brand-400')
                                              }
                                            >
                                              {opt.hex_cor && (
                                                <span
                                                  className="w-3 h-3 rounded-full border border-white/40"
                                                  style={{ backgroundColor: opt.hex_cor }}
                                                />
                                              )}
                                              {opt.label}
                                            </button>
                                          )
                                        })}
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                              {gradePendente && (
                                <p className="text-[11px] text-amber-700 font-medium mt-1.5 flex items-center gap-1">
                                  <AlertTriangle size={11} /> escolha uma opção pra cada atributo antes de finalizar
                                </p>
                              )}
                              {convAtual && (
                                <p className="text-[11px] text-ink-500 mt-1">
                                  Vendendo em <strong>{convAtual.unidade}</strong> · 1 {convAtual.unidade} = {convAtual.fator} {item.unidade_base} ·
                                  estoque convertido = {(item.estoque_atual / convAtual.fator).toFixed(2)} {convAtual.unidade}
                                </p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* --- OBSERVAÇÃO --- */}
          <div className="card p-4">
            <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
              <FileText size={14} /> Observação
            </h3>
            <textarea
              value={observacao}
              onChange={e => setObservacao(e.target.value)}
              rows={2}
              placeholder="Opcional - visível no pedido"
              className="input text-sm"
            />
          </div>
        </div>

        {/* ============ COLUNA DIREITA (resumo sticky) ============ */}
        <div className="lg:col-span-1">
          <div className="sticky top-4 space-y-3">
            {/* Resumo */}
            <div className="card p-4">
              <h3 className="font-bold text-ink-900 mb-3">Resumo do pedido</h3>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-ink-600">Subtotal</span>
                  <span className="font-medium">{formatMoney(subtotal)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-ink-600">Desconto</span>
                  <input
                    type="number"
                    value={desconto}
                    step="0.01"
                    min="0"
                    onChange={e => setDesconto(Number(e.target.value))}
                    className="w-24 text-right border-b border-ink-200 focus:border-brand-500 outline-none bg-transparent text-red-600 font-medium"
                  />
                </div>
                {/* Bike na troca abatida como desconto (informativo) — igual ao PDV */}
                {bikeTotal > 0 && (
                  <div className="flex items-center justify-between text-amber-600">
                    <span>Bicicleta na troca</span>
                    <span className="font-medium">− {formatMoney(bikeTotal)}</span>
                  </div>
                )}
                {desconto > 0 && bikeTotal > 0 && (
                  <div className="flex items-center justify-between text-amber-700 font-semibold">
                    <span>Total de desconto</span>
                    <span>− {formatMoney(desconto + bikeTotal)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-ink-600">Frete</span>
                  <input
                    type="number"
                    value={frete}
                    step="0.01"
                    min="0"
                    onChange={e => setFrete(Number(e.target.value))}
                    className="w-24 text-right border-b border-ink-200 focus:border-brand-500 outline-none bg-transparent font-medium"
                  />
                </div>
                {valorST > 0 && (
                  <div className="flex justify-between text-amber-700">
                    <span>ICMS-ST</span>
                    <span className="font-medium">+ {formatMoney(valorST)}</span>
                  </div>
                )}
                <div className="border-t pt-2 flex justify-between items-baseline">
                  <span className="font-semibold text-ink-900">TOTAL{valorST > 0 ? ' (c/ imposto)' : ''}</span>
                  <span className="text-2xl font-bold text-emerald-700">{formatMoney(total)}</span>
                </div>
                {itens.length > 0 && (
                  <p className="text-[11px] text-ink-500 text-right">
                    {itens.length} item(ns) · {itens.reduce((s, i) => s + i.quantidade, 0)} unidade(s)
                  </p>
                )}
              </div>
            </div>

            {/* Pagamento */}
            <div className="card p-4">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <CreditCard size={14} /> Forma de pagamento
              </h3>
              <div className="mb-3">
                <div className="text-xs text-ink-600 mb-1">Tipo da venda</div>
                <div className="grid grid-cols-2 gap-1.5">
                  <button type="button" onClick={() => setTipoVenda('fiscal')}
                    className={`px-2 py-2 rounded text-xs font-medium border flex items-center justify-center gap-1 transition-all ${tipoVenda === 'fiscal' ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' : 'bg-white border-ink-200 hover:border-ink-300'}`}>
                    <FileText size={12} /> Com nota (NF-e)
                  </button>
                  <button type="button" onClick={() => setTipoVenda('simples')}
                    className={`px-2 py-2 rounded text-xs font-medium border flex items-center justify-center gap-1 transition-all ${tipoVenda === 'simples' ? 'bg-slate-600 text-white border-slate-600 shadow-sm' : 'bg-white border-ink-200 hover:border-ink-300'}`}>
                    Sem nota
                  </button>
                  <button type="button" onClick={() => setTipoVenda('bonificacao')}
                    className={`px-2 py-2 rounded text-xs font-medium border flex items-center justify-center gap-1 transition-all ${tipoVenda === 'bonificacao' ? 'bg-amber-600 text-white border-amber-600 shadow-sm' : 'bg-white border-ink-200 hover:border-ink-300'}`}>
                    🎁 Bonificação (NF)
                  </button>
                  <button type="button" onClick={() => setTipoVenda('bonificacao_simples')}
                    className={`px-2 py-2 rounded text-xs font-medium border flex items-center justify-center gap-1 transition-all ${tipoVenda === 'bonificacao_simples' ? 'bg-amber-500 text-white border-amber-500 shadow-sm' : 'bg-white border-ink-200 hover:border-ink-300'}`}>
                    🎁 Bonif. simples
                  </button>
                </div>
                <div className="mt-3">
                  <div className="text-xs text-ink-600 mb-1">Canal</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button type="button" onClick={() => setCanal('digitado')}
                      className={`px-2 py-2 rounded text-xs font-medium border flex items-center justify-center gap-1 transition-all ${canal === 'digitado' ? 'bg-teal-600 text-white border-teal-600 shadow-sm' : 'bg-white border-ink-200 hover:border-ink-300'}`}>
                      💻 Digitado
                    </button>
                    <button type="button" onClick={() => setCanal('celular')}
                      className={`px-2 py-2 rounded text-xs font-medium border flex items-center justify-center gap-1 transition-all ${canal === 'celular' ? 'bg-teal-600 text-white border-teal-600 shadow-sm' : 'bg-white border-ink-200 hover:border-ink-300'}`}>
                      📱 Celular
                    </button>
                  </div>
                </div>
                {tipoVenda.startsWith('bonificacao') && (
                  <div className="mt-2 space-y-2 bg-amber-50 border border-amber-200 rounded p-2">
                    <div className="text-[11px] text-amber-800">{tipoVenda === 'bonificacao' ? 'Emite NF de bonificação (CFOP 5910/6910) + baixa estoque.' : 'Só baixa estoque (sem nota).'} Sem financeiro. Cobrança = BNF.</div>
                    <input className="input w-full text-sm" placeholder="Nº do pedido de venda referente (opcional)"
                      value={refNumero} onChange={e => setRefNumero(e.target.value)} />
                    <textarea className="input w-full text-sm" rows={2} placeholder="Motivo da bonificação (por quê?) *"
                      value={motivoBonif} onChange={e => setMotivoBonif(e.target.value)} />
                  </div>
                )}
              </div>
              <label className="block mb-3">
                <div className="text-xs text-ink-600 mb-1">Vendedor da venda</div>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                  <input
                    value={abrirVendedor ? buscaVendedor : vendedorNome}
                    onChange={e => { setBuscaVendedor(e.target.value); setAbrirVendedor(true); setDestaqueVend(0) }}
                    onFocus={() => { setBuscaVendedor(''); setAbrirVendedor(true); setDestaqueVend(0) }}
                    onBlur={() => setTimeout(() => setAbrirVendedor(false), 150)}
                    // ↓↑ navega (inclui "Eu"/"Sem"), Enter seleciona, Esc fecha
                    onKeyDown={navegarLista(vendOpcoes, destaqueVend, setDestaqueVend,
                      o => { setVendedorId(o.id); setAbrirVendedor(false) }, () => setAbrirVendedor(false))}
                    placeholder="Buscar vendedor por nome ou código..."
                    className="input pl-9 text-sm w-full"
                    autoComplete="off"
                  />
                  {abrirVendedor && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-10 max-h-72 overflow-y-auto">
                      <button type="button"
                        onMouseDown={() => { setVendedorId(''); setAbrirVendedor(false); }}
                        onMouseEnter={() => setDestaqueVend(0)}
                        className={`w-full px-3 py-2 text-left text-sm border-b ${destaqueVend === 0 ? 'bg-brand-50' : 'hover:bg-ink-50'}`}>Eu (usuário logado)</button>
                      <button type="button"
                        onMouseDown={() => { setVendedorId('sem'); setAbrirVendedor(false); }}
                        onMouseEnter={() => setDestaqueVend(1)}
                        className={`w-full px-3 py-2 text-left text-sm border-b ${destaqueVend === 1 ? 'bg-brand-50' : 'hover:bg-ink-50'}`}>Sem vendedor (sem comissão)</button>
                      {vendedoresFiltrados.map((v: any, idx: number) => (
                        <button key={v.id} type="button"
                          onMouseDown={() => { setVendedorId(String(v.id)); setAbrirVendedor(false); }}
                          onMouseEnter={() => setDestaqueVend(idx + 2)}
                          ref={el => { if (idx + 2 === destaqueVend) el?.scrollIntoView({ block: 'nearest' }) }}
                          className={`w-full px-3 py-2 text-left text-sm border-b last:border-0 flex items-center gap-2 ${idx + 2 === destaqueVend ? 'bg-brand-50' : 'hover:bg-ink-50'}`}>
                          {v.codigo && <span className="text-[10px] font-mono bg-ink-100 text-ink-600 px-1 rounded">{v.codigo}</span>}
                          <span className="truncate">{v.nome}</span>
                        </button>
                      ))}
                      {buscaVendedor.trim() && vendedoresFiltrados.length === 0 && (
                        <div className="px-3 py-2 text-xs text-ink-500">Nenhum vendedor encontrado</div>
                      )}
                    </div>
                  )}
                </div>
              </label>
              {cliente && (
                <p className="text-[11px] text-ink-600 bg-ink-50 rounded px-2 py-1 mb-2">
                  Cliente nível <strong>{NIVEL_LABEL[(cliente as any).nivel_cobranca || 'avista']}</strong> — pode pagar nesse nível e abaixo. Ajuste na Análise de Crédito.
                </p>
              )}
              {ehBonificacao ? (
                <div className="text-xs text-ink-600 bg-amber-50 border border-amber-200 rounded p-2">
                  Bonificação não gera financeiro — sem composição de pagamento.
                </div>
              ) : (
                <ComposicaoPagamento
                  total={total}
                  nivelCliente={(cliente as any)?.nivel_cobranca || 'avista'}
                  pagamentos={pagamentos}
                  setPagamentos={setPagamentos}
                />
              )}

              {pagamentos.some(p => p.forma === 'boleto') && (
                <div className="mb-1 mt-2">
                  <label className="text-xs text-ink-600">Banco emissor do boleto</label>
                  <select className="input w-full mt-1 text-sm" value={contaBancariaId}
                    onChange={(e) => setContaBancariaId(e.target.value ? Number(e.target.value) : '')}>
                    <option value="">— escolha o banco —</option>
                    {contasBanco.filter((c: any) => c.emite_boleto).map((c: any) => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-ink-500 mt-1">O boleto será gerado por este banco ao faturar.</p>
                </div>
              )}
            </div>

            {/* Alertas */}
            {alertas.length > 0 && (
              <div className="card p-3 border-amber-200 bg-amber-50">
                <div className="flex items-center gap-2 mb-1 text-amber-900 font-semibold text-xs">
                  <AlertCircle size={12} /> Pendências
                </div>
                <ul className="space-y-0.5">
                  {alertas.map((a, i) => (
                    <li key={i} className="text-xs text-amber-900 flex items-start gap-1">
                      <span>•</span><span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Info: itens industriais vão virar OP proposta */}
            {itensSobProducao.length > 0 && (
              <div className="card p-3 border-sky-200 bg-sky-50">
                <div className="flex items-center gap-2 mb-1 text-sky-900 font-semibold text-xs">
                  ⚙ {itensSobProducao.length} item(ns) industrial(is) sem estoque
                </div>
                <div className="text-xs text-sky-900">
                  Ao confirmar o pedido, será gerada uma <strong>Ordem de Produção proposta</strong> para o PCP aprovar.
                </div>
              </div>
            )}

            {/* Botão criar — abre modal pré-gravação primeiro */}
            <button
              disabled={!podeEnviar || criar.isPending}
              onClick={() => iniciarSalvar()}
              className={`w-full py-3 rounded-lg font-bold text-base flex items-center justify-center gap-2 transition-all ${
                podeEnviar && !criar.isPending
                  ? 'bg-gradient-to-r from-emerald-500 to-emerald-700 hover:from-emerald-600 hover:to-emerald-800 text-white shadow-md'
                  : 'bg-ink-200 text-ink-400 cursor-not-allowed'
              }`}>
              {criar.isPending ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle2 size={18} />}
              Criar Pedido
              <span className="text-xs opacity-70 font-mono">F12</span>
            </button>

            <button
              onClick={() => guardedNavigate(() => nav(-1))}
              className="w-full py-2 rounded-lg text-sm text-ink-600 hover:bg-ink-100">
              Cancelar
            </button>
          </div>
        </div>
      </div>

      {/* MODAL — pré-gravação: revisa qualidade vs estoque */}
      {modalPreGrava.aberto && (() => {
        const itensIndustriais = itens.filter(i => i.is_industrial)
        const algumComEstoque = itensIndustriais.some(i => {
          const s = i.saldo_qualidade
          return s && (s.PRIMEIRA + s.SEGUNDA + s.TERCEIRA) > 0
        })
        // Motivo é OBRIGATÓRIO sempre que vendedor marcar "forçar produção".
        // Não relaxa quando estoque=0 — auditoria precisa do contexto sempre.
        const precisaMotivo = modalPreGrava.forcouProducao && !modalPreGrava.motivo.trim()
        return (
          <div className="fixed inset-0 bg-ink-950/85 z-[60] flex items-center justify-center p-4"
            role="dialog" aria-modal="true" aria-labelledby="modal-pregrava-titulo">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-ink-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-xl">📦</div>
                  <div className="flex-1">
                    <h3 id="modal-pregrava-titulo" className="text-lg font-bold text-ink-900">Revisão antes de gravar</h3>
                    <p className="text-xs text-ink-500">Confira saldo de estoque e qualidade exigida pelo cliente</p>
                  </div>
                  <button onClick={() => setModalPreGrava(s => ({ ...s, aberto: false }))}
                    className="p-1.5 rounded-lg hover:bg-ink-100 text-ink-500">
                    <X size={18} />
                  </button>
                </div>
              </div>

              <div className="p-5 space-y-3">
                {modalPreGrava.carregando && (
                  <div className="flex items-center justify-center py-8 text-ink-500">
                    <Loader2 className="animate-spin mr-2" size={18} /> Consultando saldos…
                  </div>
                )}
                {!modalPreGrava.carregando && itensIndustriais.length === 0 && (
                  <p className="text-sm text-ink-600 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    Nenhum item industrial. Não há produção envolvida — confirma o pedido?
                  </p>
                )}
                {!modalPreGrava.carregando && itens.map((i, idx) => {
                  if (!i.is_industrial) return null
                  const s = i.saldo_qualidade || { PRIMEIRA: 0, SEGUNDA: 0, TERCEIRA: 0 }
                  const total = s.PRIMEIRA + s.SEGUNDA + s.TERCEIRA
                  const exigida = i.qualidade_exigida || 'PRIMEIRA'
                  const disponivelExigida = s[exigida]
                  return (
                    <div key={idx} className="border border-ink-200 rounded-lg p-3">
                      <div className="font-medium text-ink-900 text-sm mb-1">{i.descricao}</div>
                      <div className="text-xs text-ink-500 mb-2">
                        Pedido: <strong>{i.quantidade} {i.unidade_base}</strong>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {(['PRIMEIRA', 'SEGUNDA', 'TERCEIRA'] as const).map(q => (
                          <div key={q} className={`rounded-lg border p-2 text-center ${
                            s[q] > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-ink-50 border-ink-200'
                          }`}>
                            <div className="text-[10px] uppercase font-semibold text-ink-600">{q}</div>
                            <div className="text-base font-bold text-ink-900">{s[q].toFixed(2)}</div>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="text-xs text-ink-600">
                          Cliente exige:
                          <span className="ml-2 inline-flex gap-1">
                            {(['PRIMEIRA', 'SEGUNDA', 'TERCEIRA'] as const).map(q => (
                              <button key={q} type="button"
                                onClick={() => alterarQualidadeExigida(idx, q)}
                                className={'px-2 py-0.5 rounded-full text-xs font-medium border transition ' +
                                  (exigida === q
                                    ? 'bg-brand-600 text-white border-brand-600'
                                    : 'bg-white text-ink-700 border-ink-300 hover:border-brand-400')}>
                                {q[0]}{q.slice(1).toLowerCase()}
                              </button>
                            ))}
                          </span>
                        </div>
                        <div className="text-xs">
                          {disponivelExigida >= i.quantidade ? (
                            <span className="text-emerald-700 font-semibold">✓ Estoque cobre</span>
                          ) : disponivelExigida > 0 ? (
                            <span className="text-amber-700 font-semibold">⚠ Cobre {disponivelExigida} de {i.quantidade}</span>
                          ) : total > 0 ? (
                            <span className="text-amber-700 font-semibold">⚠ Sem {exigida.toLowerCase()}, mas tem {total} em outras qualidades</span>
                          ) : (
                            <span className="text-ink-500">Sem estoque, vai produzir</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}

                {algumComEstoque && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
                    <div className="font-semibold text-amber-900 mb-1">Tem estoque disponível</div>
                    <p className="text-amber-800 mb-2">
                      Sistema vai usar primeiro o estoque na qualidade exigida e produzir só o faltante.
                      Se quiser <strong>produzir tudo mesmo tendo estoque</strong>, marque abaixo (vai gerar log de auditoria).
                    </p>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={modalPreGrava.forcouProducao}
                        onChange={e => setModalPreGrava(s => ({ ...s, forcouProducao: e.target.checked }))}
                        className="w-4 h-4" />
                      <span className="text-amber-900 font-medium">Forçar produção, ignorando estoque</span>
                    </label>
                    {modalPreGrava.forcouProducao && (
                      <textarea
                        value={modalPreGrava.motivo}
                        onChange={e => setModalPreGrava(s => ({ ...s, motivo: e.target.value }))}
                        placeholder="Motivo (obrigatório): cliente exigiu lote novo, cor não bate, etc."
                        rows={2}
                        className="mt-2 w-full text-xs border border-amber-300 rounded p-2 focus:border-amber-500 outline-none"
                      />
                    )}
                  </div>
                )}
              </div>

              {modalPreGrava.erroSaldo && !modalPreGrava.carregando && (
                <div className="mx-5 mb-3 bg-red-50 border border-red-300 text-red-800 text-xs rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <div>
                    <strong>Falha ao consultar saldo de estoque.</strong> Não dá pra confirmar
                    sem ver os saldos — você ficaria às cegas. Tente "Voltar" e clicar em Criar de novo.
                  </div>
                </div>
              )}

              <div className="p-5 border-t border-ink-200 flex gap-2">
                <button onClick={() => {
                    if (modalPreGrava.motivo.trim() && !confirm('Você digitou um motivo. Sair sem salvar?')) return
                    setModalPreGrava(s => ({ ...s, aberto: false, motivo: '', forcouProducao: false }))
                  }}
                  className="flex-1 py-2.5 rounded-lg border border-ink-300 text-ink-700 font-semibold hover:bg-ink-50">
                  Voltar
                </button>
                <button
                  disabled={modalPreGrava.carregando || precisaMotivo || criar.isPending || modalPreGrava.erroSaldo}
                  onClick={() => {
                    setModalPreGrava(s => ({ ...s, aberto: false }))
                    criar.mutate()
                  }}
                  className={'flex-1 py-2.5 rounded-lg font-semibold ' +
                    (modalPreGrava.carregando || precisaMotivo || criar.isPending || modalPreGrava.erroSaldo
                      ? 'bg-ink-200 text-ink-400 cursor-not-allowed'
                      : 'bg-emerald-600 hover:bg-emerald-700 text-white')}>
                  {modalPreGrava.erroSaldo ? 'Erro ao carregar saldo'
                    : precisaMotivo ? 'Informe o motivo'
                    : 'Confirmar e gravar'}
                </button>
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}

// ── Compositor de múltiplos pagamentos (espelha o PDV, sem as formas de caixa) ──
// Monta a lista `pagamentos`: cada forma vira um título próprio no faturamento.
function ComposicaoPagamento({ total, nivelCliente, pagamentos, setPagamentos }: {
  total: number
  nivelCliente: string
  pagamentos: LinhaPag[]
  setPagamentos: (l: LinhaPag[]) => void
}) {
  const { data: cfgFormas = [] } = useQuery<any[]>({
    queryKey: ['config-formas-novopedido'],
    queryFn: async () => (await api.get('/caixa/config-formas')).data,
  })
  const totalPago = pagamentos.reduce((s, p) => s + (p.valor || 0), 0)
  const saldoRestante = Math.max(0, Math.round((total - totalPago) * 100) / 100)

  // Formas: ativas, fora as exclusivas de caixa.
  // BUG: o Pedido travava as formas pelo nível de crédito do cliente
  //   (.filter FORMA_RANK <= NIVEL_RANK[nivelCliente]); como o nível default é
  //   'avista' (rank 1), o "A prazo" (e boleto/cheque) nunca aparecia, ao
  //   contrário do PDV, que mostra todas as formas ativas.
  // FIX (30/06): removido o filtro por nível p/ paridade total com o PDV — todas
  //   as formas ativas aparecem sempre. nivelCliente fica sem uso aqui de
  //   propósito (controle de crédito não trava mais o Pedido).
  const formas = useMemo(() => (cfgFormas as any[])
    .filter(c => c.ativo && !PAG_SO_CAIXA.has(c.forma))
    .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
    .map(c => ({ id: c.forma as string, label: c.label as string, a_prazo: !!c.a_prazo })),
    [cfgFormas])

  const [formaAtual, setFormaAtual] = useState('')
  const [valorAtual, setValorAtual] = useState('')
  const [parcelasAtual, setParcelasAtual] = useState(1)
  const [vencAtual, setVencAtual] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10)
  })
  const [bikeAtual, setBikeAtual] = useState<BikeRecebida>({})

  const cfgAtual = formas.find(f => f.id === formaAtual)
  const ehBike = formaAtual === 'recebimento_bike'
  const parcelavel = !!cfgAtual && !ehBike && (cfgAtual.a_prazo || formaAtual === 'cartao_credito')
  const comVenc = !!cfgAtual && !ehBike && cfgAtual.a_prazo

  function addLinha(forma: string, label: string, valor: number, parcelas: number, primeiro_venc?: string, bike?: BikeRecebida) {
    if (valor <= 0) { toast.error('Informe um valor'); return }
    setPagamentos([...pagamentos, { forma, label, valor: Math.round(valor * 100) / 100, parcelas, primeiro_venc, bike }])
    setFormaAtual(''); setValorAtual(''); setParcelasAtual(1); setBikeAtual({})
  }

  function escolherForma(id: string) {
    // BUG: formas simples (dinheiro/pix/débito) eram adicionadas direto com o saldo
    //   INTEIRO, sem abrir o campo de valor → impossível dividir o pagamento
    //   (ex.: parte em pix, parte em dinheiro): a 1ª forma já levava tudo.
    // FIX (30/06): igual ao PDV (escolherForma), sempre abre o campo com o saldo
    //   restante sugerido (editável); o operador ajusta o valor e confirma, podendo
    //   compor várias formas até cobrir o total.
    setFormaAtual(id)
    setParcelasAtual(1)
    setBikeAtual({})
    // Bike não sugere o saldo: o valor é quanto a bike vale na troca, não a venda.
    setValorAtual(id === 'recebimento_bike' ? '' : (saldoRestante > 0 ? saldoRestante.toFixed(2) : ''))
  }

  function confirmarLinha() {
    const v = parseFloat(valorAtual.replace(',', '.')) || 0
    if (!formaAtual || v <= 0) { toast.error('Informe forma e valor'); return }
    if (ehBike && (!bikeAtual.marca?.trim() || !bikeAtual.modelo?.trim())) {
      toast.error('Informe marca e modelo da bike recebida'); return
    }
    addLinha(formaAtual, cfgAtual?.label || formaAtual, v, parcelavel ? parcelasAtual : 1,
      comVenc ? vencAtual : undefined, ehBike ? { ...bikeAtual } : undefined)
  }

  return (
    <div className="space-y-2">
      {pagamentos.length > 0 && (
        <div className="space-y-1">
          {pagamentos.map((p, idx) => {
            const Icon = pagIcon(p.forma)
            return (
              <div key={idx} className="flex items-center gap-2 text-xs bg-ink-50 rounded px-2 py-1.5">
                <Icon size={13} className="text-ink-500 shrink-0" />
                <span className="flex-1 truncate">
                  {p.label}{p.parcelas > 1 ? ` ${p.parcelas}x` : ''}
                  {p.bike ? ` · ${[p.bike.marca, p.bike.modelo].filter(Boolean).join(' ')}` : ''}
                  {p.primeiro_venc ? ` · venc ${p.primeiro_venc.split('-').reverse().join('/')}` : ''}
                </span>
                <span className="font-semibold tabular-nums">{formatMoney(p.valor)}</span>
                <button type="button" onClick={() => setPagamentos(pagamentos.filter((_, i) => i !== idx))}
                  className="text-ink-400 hover:text-red-600"><X size={13} /></button>
              </div>
            )
          })}
        </div>
      )}

      <div className={`text-xs rounded px-2 py-1.5 flex justify-between ${saldoRestante > 0.01 ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800'}`}>
        <span>{saldoRestante > 0.01 ? 'Falta pagar' : 'Pagamento completo'}</span>
        <span className="font-bold tabular-nums">{formatMoney(saldoRestante)}</span>
      </div>

      <div className="grid grid-cols-2 gap-1.5">
        {formas.map(f => {
          const Icon = pagIcon(f.id)
          // Cada forma com sua cor (igual ao PDV); a selecionada ganha um ring.
          return (
            <button key={f.id} type="button" onClick={() => escolherForma(f.id)}
              className={`px-2 py-2 rounded text-xs flex items-center gap-1 text-white font-medium transition-all ${pagColor(f.id)} ${
                formaAtual === f.id ? 'ring-2 ring-offset-1 ring-brand-500 shadow-sm' : 'opacity-90 hover:opacity-100'
              }`}>
              <Icon size={12} /> {f.label}
            </button>
          )
        })}
      </div>

      {formaAtual && (
        <div className="border border-brand-200 bg-brand-50/50 rounded p-2 space-y-2">
          <div className="flex items-center gap-2">
            <label className="text-[11px] text-ink-600 w-16">{ehBike ? 'Vl. troca' : 'Valor'}</label>
            <input className="input text-sm flex-1" inputMode="decimal" value={valorAtual} autoFocus
              onChange={e => setValorAtual(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); confirmarLinha() } }} />
          </div>
          {parcelavel && (
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-ink-600 w-16">Parcelas</label>
              <div className="flex gap-1 flex-1">
                {[1, 2, 3, 4, 6, 10, 12].map(n => (
                  <button key={n} type="button" onClick={() => setParcelasAtual(n)}
                    className={`flex-1 py-1 text-[11px] rounded border ${parcelasAtual === n ? 'bg-brand-500 text-white border-brand-500' : 'bg-white border-ink-200'}`}>
                    {n}x
                  </button>
                ))}
              </div>
            </div>
          )}
          {comVenc && (
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-ink-600 w-16">1º venc.</label>
              <input type="date" className="input text-sm flex-1" value={vencAtual} onChange={e => setVencAtual(e.target.value)} />
            </div>
          )}
          {ehBike && (
            <div className="space-y-1.5 border-t border-brand-200 pt-2">
              <div className="text-[11px] text-ink-600 font-medium">Bike recebida (vira seminova no estoque)</div>
              <div className="grid grid-cols-2 gap-1.5">
                <input className="input text-sm" placeholder="Marca *" value={bikeAtual.marca || ''}
                  onChange={e => setBikeAtual({ ...bikeAtual, marca: e.target.value })} />
                <input className="input text-sm" placeholder="Modelo *" value={bikeAtual.modelo || ''}
                  onChange={e => setBikeAtual({ ...bikeAtual, modelo: e.target.value })} />
                <input className="input text-sm" placeholder="Ano" value={bikeAtual.ano || ''}
                  onChange={e => setBikeAtual({ ...bikeAtual, ano: e.target.value })} />
                <input className="input text-sm" placeholder="Tamanho" value={bikeAtual.tamanho || ''}
                  onChange={e => setBikeAtual({ ...bikeAtual, tamanho: e.target.value })} />
                <input className="input text-sm" placeholder="Cor" value={bikeAtual.cor || ''}
                  onChange={e => setBikeAtual({ ...bikeAtual, cor: e.target.value })} />
                <input className="input text-sm" placeholder="Nº de série" value={bikeAtual.numero_serie || ''}
                  onChange={e => setBikeAtual({ ...bikeAtual, numero_serie: e.target.value })} />
              </div>
              <input className="input text-sm w-full" placeholder="Observações" value={bikeAtual.observacoes || ''}
                onChange={e => setBikeAtual({ ...bikeAtual, observacoes: e.target.value })} />
            </div>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={() => { setFormaAtual(''); setValorAtual('') }}
              className="flex-1 py-1.5 text-xs rounded border border-ink-200 bg-white">Cancelar</button>
            <button type="button" onClick={confirmarLinha}
              className="flex-1 py-1.5 text-xs rounded bg-brand-500 text-white font-medium flex items-center justify-center gap-1">
              <Plus size={12} /> Adicionar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
